/**
 * GLYDE Voice SDK - useTextChat Hook
 * 
 * React hook for managing text chat state and lifecycle.
 * 
 * @example
 * const { messages, isLoading, sendMessage, clearHistory } = useTextChat({
 *   publishableKey: 'pk_...',
 *   contextId: 'application-uuid'
 * });
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { GlydeText } from '../core/GlydeText';
import type { GlydeTextConfig, ChatMessage, AgentMetadata, ContextSwitchPayload } from '../core/types';

/**
 * Text chat status
 */
export type TextChatStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Text chat hook configuration
 */
export interface UseTextChatConfig extends Omit<GlydeTextConfig, 'onEvent' | 'onMessage' | 'onTyping'> {
  /** Auto-initialize the chat on mount */
  autoInit?: boolean;
  /** Callback when context switches (e.g. screening -> candidate_discover) */
  onContextSwitch?: (payload: ContextSwitchPayload) => void;
}

/**
 * Text chat hook return value
 */
export interface UseTextChatReturn {
  /** Current status of the text chat */
  status: TextChatStatus;
  /** Chat message history */
  messages: ChatMessage[];
  /** Whether the agent is typing/processing */
  isLoading: boolean;
  /** Error message if status is 'error' */
  errorMessage: string | null;
  /** Agent metadata from voice config (agent_name, company_name, etc.) */
  agentMetadata: AgentMetadata | null;
  /** Initialize the chat session */
  initialize: () => Promise<void>;
  /** Send a message */
  sendMessage: (message: string) => Promise<void>;
  /** Clear local message history */
  clearHistory: () => void;
  /** The underlying GlydeText instance */
  textChat: GlydeText | null;
}

/**
 * React hook for managing text chat state and lifecycle
 * 
 * @param config - Text chat configuration
 * @returns Text chat state and control methods
 */
export function useTextChat(config: UseTextChatConfig): UseTextChatReturn {
  const [status, setStatus] = useState<TextChatStatus>('idle');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [agentMetadata, setAgentMetadata] = useState<AgentMetadata | null>(null);
  
  const textChatRef = useRef<GlydeText | null>(null);
  const initializedRef = useRef(false);
  const initInFlightRef = useRef(false);
  const sendingRef = useRef(false);
  const autoInitAttemptKeyRef = useRef<string | null>(null);

  /**
   * Get or create GlydeText instance
   */
  const getTextChat = useCallback(() => {
    if (!textChatRef.current) {
      textChatRef.current = new GlydeText({
        ...config,
        onTyping: setIsLoading,
        onContextSwitch: config.onContextSwitch,
      });
    }
    return textChatRef.current;
  }, [config]);

  /**
   * Initialize the chat session.
   * Guards against concurrent calls via initInFlightRef.
   */
  const initialize = useCallback(async () => {
    if (initializedRef.current || initInFlightRef.current) return;
    initInFlightRef.current = true;
    
    setStatus('loading');
    setErrorMessage(null);

    try {
      const chat = getTextChat();
      const history = await chat.initialize();
      setMessages(history);

      const meta = chat.getAgentMetadata();
      if (meta) setAgentMetadata(meta);

      setStatus('ready');
      initializedRef.current = true;
      
    } catch (error) {
      console.error('[useTextChat] Initialization error:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to initialize chat');
    } finally {
      initInFlightRef.current = false;
    }
  }, [getTextChat]);

  /**
   * Send a message.
   *
   * Ensures initialization is complete before sending. Uses a sendingRef
   * guard to prevent double-sends from rapid Enter key presses.
   * Adds the user message optimistically, then appends only the AI response.
   */
  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim() || sendingRef.current) return;
    sendingRef.current = true;
    
    setErrorMessage(null);

    try {
      const chat = getTextChat();

      // Ensure init has completed before we send; if auto-init is still in
      // flight, wait for it rather than triggering a second initialize.
      if (!initializedRef.current) {
        await initialize();
      }
      
      const userMessage: ChatMessage = {
        role: 'user',
        content: message.trim(),
        created: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);
      
      const aiResponse = await chat.sendMessage(message);
      
      setMessages(prev => [...prev, aiResponse]);

      if (status !== 'ready') {
        setStatus('ready');
        initializedRef.current = true;
      }
      
    } catch (error) {
      console.error('[useTextChat] Send message error:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      sendingRef.current = false;
    }
  }, [getTextChat, status, initialize]);

  /**
   * Clear local message history
   */
  const clearHistory = useCallback(() => {
    textChatRef.current?.clearHistory();
    setMessages([]);
    initializedRef.current = false;
    autoInitAttemptKeyRef.current = null;
    setStatus('idle');
  }, []);

  /**
   * Auto-initialize if configured
   */
  useEffect(() => {
    const autoInitKey = [
      config.contextType || 'discovery',
      config.contextId || '',
      config.unityBaseUrl || '',
      config.publishableKey || '',
      config.apiKey || '',
      config.authToken || ''
    ].join('|');

    if (
      config.autoInit &&
      !initializedRef.current &&
      config.contextId &&
      autoInitAttemptKeyRef.current !== autoInitKey
    ) {
      autoInitAttemptKeyRef.current = autoInitKey;
      initialize();
    }
  }, [
    config.autoInit,
    config.contextType,
    config.contextId,
    config.unityBaseUrl,
    config.publishableKey,
    config.apiKey,
    config.authToken,
    initialize
  ]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      textChatRef.current = null;
    };
  }, []);

  return {
    status,
    messages,
    isLoading,
    errorMessage,
    agentMetadata,
    initialize,
    sendMessage,
    clearHistory,
    textChat: textChatRef.current,
  };
}
